import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Toast } from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import Drawer from './Drawer';
import {
  listProducts,
  listFamilies,
  createProduct,
  createEnrichJob,
  createImageJob,
  subscribeToEnrichJobStream,
} from '../../services/pimApi';
import { listTagGroups } from '../../services/tagsApi';
import './pim.css';
import './pim-oc.css';

/* PIM product grid — Operations Console redesign.
   Data flows are unchanged from the previous grid: listProducts with the
   same filter params, families list, create drawer, and the two bulk job
   flows (enrich / images) with SSE progress. Gap chips, the stat tiles and
   the enrichment banner all read the server-computed summary and per-row
   publication payload, so the UI can never disagree with the completeness
   recompute / publish gate. */

const PAGE = 25;
const PAGE_CAP = 500;

const ATTRIBUTE_LABELS = {
  product_name: 'Name',
  name: 'Name',
  description: 'Description',
  primary_image: 'Image',
  list_price: 'Price',
};
// Gap chips the enrichment job can write itself (name/description/categories/
// family) render in the accent style; image/price and anything else are plain.
const AUTO_GAPS = new Set(['product_name', 'name', 'description', 'family', 'cats']);
const GAP_ORDER = ['product_name', 'name', 'description', 'primary_image', 'list_price', 'cats', 'family'];

const gapLabel = (code) =>
  ATTRIBUTE_LABELS[code] || code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function rowGaps(r) {
  const missing = new Set();
  for (const reg of Object.values(r.regions || {})) {
    for (const code of reg.missing_required || []) missing.add(code);
  }
  if (!r.family_code) missing.add('family');
  if (!r.cats) missing.add('cats');
  return [...missing]
    .map((code) => ({ key: code, label: gapLabel(code), auto: AUTO_GAPS.has(code) }))
    .sort((a, b) => {
      const ia = GAP_ORDER.indexOf(a.key);
      const ib = GAP_ORDER.indexOf(b.key);
      return (ia === -1 ? GAP_ORDER.length : ia) - (ib === -1 ? GAP_ORDER.length : ib);
    });
}

function rowPct(r) {
  const vals = Object.values(r.regions || {}).map((x) => x.completeness ?? 0);
  return vals.length ? Math.min(...vals) : 0;
}

function pctBreakdown(r) {
  const parts = Object.entries(r.regions || {}).map(([m, x]) => `${m} ${x.completeness ?? 0}%`);
  return parts.length ? parts.join(' · ') : 'No publication rows';
}

function regionChip(r, market) {
  const reg = (r.regions || {})[market];
  if (!reg) return { cls: 'off', label: '—', title: `${market}: not set up yet` };
  return reg.published
    ? { cls: 'live', label: 'Live', title: `${market}: published` }
    : { cls: 'draft', label: 'Draft', title: `${market}: not published` };
}

function pctClass(pct) {
  return pct === 100 ? 'full' : pct >= 67 ? 'mid' : 'low';
}

function formatUpdated(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function JobProgressBanner({ progress, jobId }) {
  const c = progress?.counters || {};
  return (
    <div className="oc-review-banner">
      <span className="oc-review-headline">
        Job #{jobId}: {progress?.status || 'running'}
        {c.current_sku ? ` · ${c.current_sku}` : ''}
      </span>
      <span className="oc-review-copy">
        {typeof c.processed === 'number' ? `${c.processed}/${c.eligible ?? '?'} processed` : 'Starting…'}
        {typeof c.enriched === 'number' ? ` · ${c.enriched} enriched` : ''}
        {typeof c.skipped === 'number' && c.skipped ? ` · ${c.skipped} skipped` : ''}
        {typeof c.errors === 'number' && c.errors ? ` · ${c.errors} errors` : ''}
      </span>
      <Link to="/jobs/history?queue=pim-enrich" className="oc-btn oc-btn-secondary oc-review-action">
        View jobs
      </Link>
    </div>
  );
}

export default function PimProducts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qDraft, setQDraft] = useState('');
  const q = useDebouncedValue(qDraft);
  const [familyCode, setFamilyCode] = useState('');
  const [published, setPublished] = useState('');
  const [gap, setGap] = useState('all'); // all | ready | copy | img | price
  // Default hides internal (PIM-only) products; toggle to view or isolate them.
  const [internalFilter, setInternalFilter] = useState('catalog');
  // Tag spec ("trade_channel:swimart"), qualified so a value code shared by two
  // groups still means one thing.
  const [tag, setTag] = useState('');
  const [tagGroups, setTagGroups] = useState([]);
  const [sort, setSort] = useState('sku'); // sku | name | completeness
  const [dir, setDir] = useState('asc');
  const [families, setFamilies] = useState([]);
  const [limit, setLimit] = useState(PAGE);
  const [offset, setOffset] = useState(0);
  const [toast, setToast] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newSku, setNewSku] = useState('');
  const [newFamily, setNewFamily] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'filter' | 'images' | null
  const [bulkLimit, setBulkLimit] = useState(100);
  const [bulkForce, setBulkForce] = useState(false);
  const [imageAutoCommit, setImageAutoCommit] = useState(false);
  const [enrichJob, setEnrichJob] = useState(null);
  const [enrichProgress, setEnrichProgress] = useState(null);

  const load = useCallback(async (l = limit, o = offset) => {
    setLoading(true);
    try {
      const params = { limit: l, offset: o, sort, dir };
      if (q) params.q = q;
      if (familyCode) params.family_code = familyCode;
      if (published !== '') params.published = published === 'true';
      if (gap === 'ready') params.gap_state = 'ready';
      else if (gap !== 'all') params.missing = gap; // copy | img | price — same predicates as the jobs
      if (internalFilter === 'catalog') params.is_internal = false;
      else if (internalFilter === 'internal') params.is_internal = true;
      if (tag) params.tag = tag;
      const data = await listProducts(params);
      setRows(data.products);
      setTotal(data.total);
      setSummary(data.summary || null);
    } catch (e) {
      setToast({ message: 'Failed to load products', error: true });
    } finally {
      setLoading(false);
    }
  }, [limit, offset, q, familyCode, published, gap, internalFilter, tag, sort, dir]);

  useEffect(() => {
    load(limit, offset);
  }, [load, limit, offset]);

  useEffect(() => {
    listFamilies().then(setFamilies).catch(() => {});
    listTagGroups({ entity_type: 'product' }).then(setTagGroups).catch(() => {});
  }, []);

  // Reset paging + selection whenever the result set changes (filters/sort).
  const resetView = () => {
    setLimit(PAGE);
    setOffset(0);
    setSelected(new Set());
  };

  const clearFilters = () => {
    setQDraft('');
    setFamilyCode('');
    setPublished('');
    setGap('all');
    setInternalFilter('catalog');
    setTag('');
    setSort('sku');
    setDir('asc');
    resetView();
  };

  const onSort = (col) => {
    if (col === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(col);
      setDir(col === 'completeness' ? 'desc' : 'asc');
    }
    resetView();
  };

  const setGapFilter = (g) => {
    setGap(g);
    resetView();
  };

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.identifier));
  const toggleRow = (sku) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) rows.forEach((r) => next.delete(r.identifier));
      else rows.forEach((r) => next.add(r.identifier));
      return next;
    });
  };

  const selectedArr = [...selected];
  const selectedRows = rows.filter((r) => selected.has(r.identifier));
  const selectedAutoGaps = selectedRows.reduce(
    (n, r) => n + rowGaps(r).filter((g) => g.auto).length,
    0
  );

  const openProduct = (sku) => navigate(`/pim/products/${encodeURIComponent(sku)}`);

  const startEnrichJob = async (payload) => {
    try {
      const job = await createEnrichJob(payload);
      setEnrichJob(job.id);
      setEnrichProgress({ status: 'pending', counters: {} });
      subscribeToEnrichJobStream(
        job.id,
        (p) => {
          setEnrichProgress(p);
          if (['succeeded', 'failed', 'cancelled'].includes(p?.status)) {
            load();
          }
        },
        () => {}
      );
      setToast({ message: `Enrichment job #${job.id} started` });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Failed to start enrichment', error: true });
    }
  };

  const startImageJob = async (payload) => {
    try {
      const job = await createImageJob(payload);
      setEnrichJob(job.id);
      setEnrichProgress({ status: 'pending', counters: {} });
      subscribeToEnrichJobStream(
        job.id,
        (p) => {
          setEnrichProgress(p);
          if (['succeeded', 'failed', 'cancelled'].includes(p?.status)) {
            load();
          }
        },
        () => {}
      );
      setToast({ message: `Image generation job #${job.id} started — review results in Image review` });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Failed to start image job', error: true });
    }
  };

  const enrichSelected = () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Enrich ${selected.size} selected SKU(s)? Uses SerpAPI + Gemini (API cost applies).`
      )
    ) {
      return;
    }
    startEnrichJob({ skus: selectedArr, force: bulkForce });
    setSelected(new Set());
  };

  const generateImagesSelected = () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Generate AI product images for ${selected.size} selected SKU(s)? Uses SerpAPI + Gemini (API cost applies). Generated images land in the review queue.`
      )
    ) {
      return;
    }
    startImageJob({ skus: selectedArr, skip_existing: false, auto_commit: false });
    setSelected(new Set());
  };

  const confirmBulkFilter = () => {
    setBulkModal(null);
    startEnrichJob({
      filter: 'needs_enrichment',
      limit: Math.min(500, Math.max(1, Number(bulkLimit) || 100)),
      force: bulkForce,
    });
  };

  const confirmImageBulkFilter = () => {
    setBulkModal(null);
    startImageJob({
      filter: 'missing_primary_image',
      limit: Math.min(500, Math.max(1, Number(bulkLimit) || 100)),
      auto_commit: imageAutoCommit,
    });
  };

  const doCreate = async () => {
    if (!newSku.trim()) return;
    try {
      const created = await createProduct({ identifier: newSku.trim(), family_code: newFamily || null });
      setCreating(false);
      setNewSku('');
      setNewFamily('');
      navigate(`/pim/products/${encodeURIComponent(created.identifier)}`);
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Create failed', error: true });
    }
  };

  const canLoadMore = total > limit && limit < PAGE_CAP;
  const deepPaging = limit >= PAGE_CAP && total > limit;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_CAP));
  const currentPage = Math.floor(offset / PAGE_CAP) + 1;

  const counts = summary || {};
  const chipDefs = [
    { id: 'all', label: 'Everything', n: counts.all },
    { id: 'ready', label: 'Ready', n: counts.ready },
    { id: 'copy', label: 'Needs copy', n: counts.auto },
    { id: 'img', label: 'No image', n: counts.img },
    { id: 'price', label: 'No price', n: counts.price },
  ];
  const tileDefs = [
    { id: 'all', value: counts.all, label: 'Products in catalog', hint: 'Staged from Epicor' },
    { id: 'ready', value: counts.ready, label: 'Ready to publish', hint: 'Every required field filled' },
    { id: 'copy', value: counts.auto, label: 'Can be auto-enriched', hint: 'Name, copy, categories, family', strong: true },
    { id: 'img', value: counts.img, label: 'Missing an image', hint: 'Generate or upload one' },
  ];

  const sortMark = (col) => (sort === col ? (dir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <div className="pim-oc">
      <div className="oc-header">
        <div className="oc-header-text">
          <h1 className="oc-title">Products</h1>
          <p className="oc-blurb">
            The catalog the storefronts read from. A product is ready to publish once it has a
            name, a description, an image, a price, categories and a family.
          </p>
        </div>
        <div className="oc-header-action" style={{ display: 'flex', gap: 'var(--oc-space-2)' }}>
          <Link to="/jobs/run/epicor" className="oc-btn oc-btn-secondary">
            Import from Epicor
          </Link>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={() => setBulkModal('images')}>
            Generate missing images
          </button>
          <button type="button" className="oc-btn oc-btn-primary" onClick={() => setCreating(true)}>
            New product
          </button>
        </div>
      </div>

      <div className="oc-stats">
        {tileDefs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={
              'oc-stat oc-blueprint' +
              (t.strong ? ' strong' : '') +
              (gap === t.id && t.id !== 'all' ? ' on' : '')
            }
            onClick={() => setGapFilter(t.id === 'all' ? 'all' : gap === t.id ? 'all' : t.id)}
          >
            <i className="oc-corner tl" /><i className="oc-corner tr" />
            <i className="oc-corner bl" /><i className="oc-corner br" />
            <span className="oc-stat-value">{t.value ?? '—'}</span>
            <span className="oc-stat-label">{t.label}</span>
            <span className="oc-stat-hint">{t.hint}</span>
          </button>
        ))}
      </div>

      {enrichJob && enrichProgress && (
        <JobProgressBanner progress={enrichProgress} jobId={enrichJob} />
      )}

      {!enrichJob && (counts.auto ?? 0) > 0 && gap !== 'ready' && (
        <div className="oc-review-banner">
          <span className="oc-review-headline">{counts.auto} products can be enriched now</span>
          <span className="oc-review-copy">
            Enrichment writes the name, description, categories and family from what Epicor
            already knows. Images and prices are handled separately.
          </span>
          <button type="button" className="oc-btn oc-btn-primary oc-review-action" onClick={() => setBulkModal('filter')}>
            Run enrichment
          </button>
        </div>
      )}

      <div className="oc-frame oc-blueprint">
        <i className="oc-corner tl" /><i className="oc-corner tr" />
        <i className="oc-corner bl" /><i className="oc-corner br" />

        <div className="oc-filterbar">
          <div className="oc-field oc-field-search">
            <label htmlFor="pim-q">Search</label>
            <input
              id="pim-q"
              className="oc-input"
              type="text"
              placeholder="SKU or name — wildcards * and ? (244*, 244?1)"
              value={qDraft}
              onChange={(e) => { setQDraft(e.target.value); resetView(); }}
            />
          </div>
          <div className="oc-field" style={{ flex: 'none', minWidth: 190 }}>
            <label htmlFor="pim-fam">Family</label>
            <select
              id="pim-fam"
              className="oc-input"
              value={familyCode}
              onChange={(e) => { setFamilyCode(e.target.value); resetView(); }}
            >
              <option value="">All families</option>
              {families.map((f) => (
                <option key={f.code} value={f.code}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="oc-field" style={{ flex: 'none', minWidth: 170 }}>
            <label htmlFor="pim-pub">Published</label>
            <select
              id="pim-pub"
              className="oc-input"
              value={published}
              onChange={(e) => { setPublished(e.target.value); resetView(); }}
            >
              <option value="">Any state</option>
              <option value="true">Live somewhere</option>
              <option value="false">Not published</option>
            </select>
          </div>
          <div className="oc-field" style={{ flex: 'none', minWidth: 170 }}>
            <label htmlFor="pim-int">Catalog</label>
            <select
              id="pim-int"
              className="oc-input"
              value={internalFilter}
              onChange={(e) => { setInternalFilter(e.target.value); resetView(); }}
            >
              <option value="catalog">Catalog (hide internal)</option>
              <option value="internal">Internal only</option>
              <option value="all">All products</option>
            </select>
          </div>
          {tagGroups.some((g) => (g.values || []).length > 0) && (
            <div className="oc-field" style={{ flex: 'none', minWidth: 180 }}>
              <label htmlFor="pim-tag">Tag</label>
              <select
                id="pim-tag"
                className="oc-input"
                value={tag}
                onChange={(e) => { setTag(e.target.value); resetView(); }}
              >
                <option value="">Any tag</option>
                {tagGroups.map((group) => (
                  <optgroup key={group.id} label={group.label}>
                    {(group.values || []).map((value) => (
                      <option key={value.id} value={`${group.code}:${value.code}`}>
                        {value.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          <div className="oc-field" style={{ flex: 1, minWidth: 260 }}>
            <span className="oc-field-label">
              What's missing{' '}
              <span
                title="Filters the list to products with a specific gap. A product with no gaps is ready to publish."
                style={{
                  display: 'inline-grid', placeItems: 'center', width: 13, height: 13,
                  border: '1px solid color-mix(in srgb, var(--oc-text) 35%, transparent)',
                  borderRadius: '50%', fontSize: 9, lineHeight: 1, cursor: 'help',
                  verticalAlign: 'middle', marginLeft: 2,
                }}
              >i</span>
            </span>
            <div className="oc-chips">
              {chipDefs.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={'oc-chip' + (gap === c.id ? ' on' : '')}
                  onClick={() => setGapFilter(c.id)}
                >
                  {c.label}{c.n != null ? ` · ${c.n}` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>

        {selected.size > 0 && (
          <div className="oc-selbar">
            <span className="oc-selbar-count">{selected.size} selected</span>
            <span className="oc-selbar-note">
              {selectedAutoGaps > 0
                ? `${selectedAutoGaps} fields across them can be written automatically`
                : 'Nothing left to enrich in this selection'}
            </span>
            <button type="button" className="oc-selbar-btn oc-selbar-btn-accent" onClick={enrichSelected}>
              Enrich selected
            </button>
            <button type="button" className="oc-selbar-btn oc-selbar-btn-ghost" onClick={generateImagesSelected}>
              Generate images
            </button>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={bulkForce} onChange={(e) => setBulkForce(e.target.checked)} />
              Force
            </label>
            <button type="button" className="oc-selbar-btn oc-selbar-btn-clear" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        )}

        <div className="oc-resultbar">
          <span className="oc-result-count">
            <strong>{total.toLocaleString()}</strong>{' '}
            <span className="oc-muted">{total === 1 ? 'product' : 'products'}</span>
          </span>
          <span className="oc-muted oc-result-note">
            {gap === 'all' && !q && !familyCode && published === '' && internalFilter === 'catalog'
              ? tag
              ? 'Filtered view'
              : `Sorted by ${sort === 'completeness' ? 'completeness' : sort} ${dir === 'asc' ? '↑' : '↓'}`
              : 'Filtered view'}
          </span>
        </div>

        <div className="oc-table-wrap">
          <div className="oc-pgrid-cols oc-pgrid-head">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              title="Select everything in view"
              aria-label="Select all in view"
            />
            <button type="button" className={'oc-sorthdr' + (sort === 'sku' ? ' on' : '')} onClick={() => onSort('sku')}>
              SKU{sortMark('sku')}
            </button>
            <button type="button" className={'oc-sorthdr' + (sort === 'name' ? ' on' : '')} onClick={() => onSort('name')}>
              Product{sortMark('name')}
            </button>
            <button type="button" className={'oc-sorthdr' + (sort === 'completeness' ? ' on' : '')} onClick={() => onSort('completeness')}>
              Complete{sortMark('completeness')}
            </button>
            <span>Still needed</span>
            <span title="Australian storefront">AU</span>
            <span title="New Zealand storefront">NZ</span>
            <span>Updated</span>
            <span></span>
          </div>

          {loading && rows.length === 0 && (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="oc-pgrid-cols oc-prow-skel">
                  <span className="oc-skel" style={{ width: 14 }} />
                  <span className="oc-skel" style={{ width: '70%' }} />
                  <span className="oc-skel" style={{ width: '85%' }} />
                  <span className="oc-skel" style={{ width: '60%' }} />
                  <span className="oc-skel" style={{ width: '75%' }} />
                  <span className="oc-skel" style={{ width: 30 }} />
                  <span className="oc-skel" style={{ width: 30 }} />
                  <span className="oc-skel" style={{ width: 45 }} />
                  <span />
                </div>
              ))}
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="oc-empty">
              <p className="oc-empty-title">Nothing matches that</p>
              <p className="oc-empty-sub">Try a shorter search, or clear the filters.</p>
              <button type="button" className="oc-btn oc-btn-secondary" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}

          {rows.map((r) => {
            const gaps = rowGaps(r);
            const pct = rowPct(r);
            const au = regionChip(r, 'AU');
            const nz = regionChip(r, 'NZ');
            const name = r.name || '';
            return (
              <div
                key={r.identifier}
                className={'oc-pgrid-cols oc-prow' + (selected.has(r.identifier) ? ' selected' : '')}
                onClick={() => openProduct(r.identifier)}
                onKeyDown={(e) => { if (e.key === 'Enter') openProduct(r.identifier); }}
                role="button"
                tabIndex={0}
              >
                <input
                  type="checkbox"
                  checked={selected.has(r.identifier)}
                  onChange={() => toggleRow(r.identifier)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select ${r.identifier}`}
                />
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                  <span className="oc-sku">{r.identifier}</span>
                  {r.is_internal && (
                    <span
                      className="oc-intbadge"
                      title="Internal-use product — staged for the internal/spares catalog, not the public storefront."
                    >
                      Int
                    </span>
                  )}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className={'oc-pname' + (name ? '' : ' missing')} title={name || undefined}>
                    {name || 'No name yet'}
                  </span>
                  <span className="oc-psub" title={(r.family_code || 'Unassigned family') || undefined}>
                    {(r.family_code || 'Unassigned family') +
                      (r.cats > 0
                        ? ` · ${r.cats} ${r.cats === 1 ? 'category' : 'categories'}`
                        : ' · not categorised') +
                      ((r.tags || []).length
                        ? ` · ${r.tags.map((t) => t.label).join(', ')}`
                        : '')}
                  </span>
                </span>
                <span className="oc-pbar-wrap" title={pctBreakdown(r)}>
                  <span className="oc-pbar">
                    <span className={`oc-pbar-fill ${pctClass(pct)}`} style={{ width: `${pct}%`, display: 'block' }} />
                  </span>
                  <span className={'oc-pbar-pct' + (pct === 100 ? ' full' : '')}>{pct}%</span>
                </span>
                <span className="oc-gaplist">
                  {gaps.length === 0 ? (
                    <span className="oc-gapchip ready">Ready to publish</span>
                  ) : (
                    gaps.map((g) => (
                      <span key={g.key} className={'oc-gapchip ' + (g.auto ? 'auto' : 'plain')}>
                        {g.label}
                      </span>
                    ))
                  )}
                </span>
                <span className={`oc-regchip ${au.cls}`} title={au.title} onClick={(e) => e.stopPropagation()}>
                  {au.label}
                </span>
                <span className={`oc-regchip ${nz.cls}`} title={nz.title} onClick={(e) => e.stopPropagation()}>
                  {nz.label}
                </span>
                <span className="oc-updated">{formatUpdated(r.updated)}</span>
                <button
                  type="button"
                  className="oc-openbtn"
                  onClick={(e) => { e.stopPropagation(); openProduct(r.identifier); }}
                >
                  Open
                </button>
              </div>
            );
          })}
        </div>

        {(canLoadMore || deepPaging) && (
          <div className="oc-pager">
            <span className="oc-pager-note oc-muted">
              {deepPaging
                ? `Page ${currentPage} of ${pageCount} · ${PAGE_CAP} per page`
                : `Showing ${rows.length} of ${total.toLocaleString()}`}
            </span>
            {canLoadMore && (
              <div className="oc-pager-btns">
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  onClick={() => setLimit((l) => Math.min(l + PAGE, PAGE_CAP))}
                >
                  Load {Math.min(PAGE, PAGE_CAP - limit)} more
                </button>
              </div>
            )}
            {deepPaging && (
              <div className="oc-pager-btns">
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_CAP))}
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  disabled={offset + PAGE_CAP >= total}
                  onClick={() => setOffset((o) => o + PAGE_CAP)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {bulkModal === 'filter' && (
        <Drawer
          title="Enrich all needing enrichment"
          sub="Uses the same eligibility rules as the “Needs copy” filter in the list."
          onClose={() => setBulkModal(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setBulkModal(null)}>Cancel</Button>
              <Button onClick={confirmBulkFilter}>Start job</Button>
            </>
          }
        >
          <div className="pim-field">
            <label>Limit (max 500)</label>
            <input
              type="number"
              min={1}
              max={500}
              value={bulkLimit}
              onChange={(e) => setBulkLimit(e.target.value)}
            />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <input type="checkbox" checked={bulkForce} onChange={(e) => setBulkForce(e.target.checked)} />
            Force re-enrich already enriched products
          </label>
          {(counts.auto ?? 0) > PAGE_CAP && (
            <p className="pim-muted" style={{ marginTop: 12 }}>
              {counts.auto.toLocaleString()} products currently match — the run processes up to
              500 per run, so run it again for the rest.
            </p>
          )}
          <p className="pim-muted" style={{ marginTop: 12 }}>
            SerpAPI + Gemini charges apply. Default concurrency is 1 SKU at a time.
          </p>
        </Drawer>
      )}

      {bulkModal === 'images' && (
        <Drawer
          title="Generate missing product images"
          sub="AI-generates blue-studio images for products with no primary image."
          onClose={() => setBulkModal(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setBulkModal(null)}>Cancel</Button>
              <Button onClick={confirmImageBulkFilter}>Start job</Button>
            </>
          }
        >
          <div className="pim-field">
            <label>Limit (max 500)</label>
            <input
              type="number"
              min={1}
              max={500}
              value={bulkLimit}
              onChange={(e) => setBulkLimit(e.target.value)}
            />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <input
              type="checkbox"
              checked={imageAutoCommit}
              onChange={(e) => setImageAutoCommit(e.target.checked)}
            />
            Auto-approve results (only ever applies to SKUs with no existing image)
          </label>
          {(counts.img ?? 0) > PAGE_CAP && (
            <p className="pim-muted" style={{ marginTop: 12 }}>
              {counts.img.toLocaleString()} products are missing an image — the run processes up
              to 500 per run, so run it again for the rest.
            </p>
          )}
          <p className="pim-muted" style={{ marginTop: 12 }}>
            Each SKU is searched on the internet for a reference photo, then redrawn on the
            standard blue studio background. Without auto-approve, results wait in the{' '}
            <Link to="/pim/image-review">Image review</Link> queue. SerpAPI + Gemini charges apply.
          </p>
        </Drawer>
      )}

      {creating && (
        <Drawer
          title="New product"
          sub="Create a draft product and start enriching it."
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={doCreate} disabled={!newSku.trim()}>Create</Button>
            </>
          }
        >
          <div className="pim-field">
            <label>SKU / identifier</label>
            <input type="text" value={newSku} onChange={(e) => setNewSku(e.target.value)} autoFocus />
          </div>
          <div className="pim-field">
            <label>Family</label>
            <select value={newFamily} onChange={(e) => setNewFamily(e.target.value)}>
              <option value="">(none)</option>
              {families.map((f) => (
                <option key={f.code} value={f.code}>{f.label}</option>
              ))}
            </select>
          </div>
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </div>
  );
}
